import CameraControls from 'camera-controls'
import type { AnyDomainEntity, Plate } from 'metalworks-domain'
import { ApplicationTypes, CustomBend, CustomResource, Handing } from 'metalworks-domain'
import { useCallback, useMemo, useState } from 'react'
import type { Scene } from 'three'
import { PerspectiveCamera } from 'three'
import { v4 } from 'uuid'

import { assertExistence, isNonNullish } from '@salsita-ai/platform-common/utils'
import type { AuthorizedPhoenixApiClient } from '@salsita-ai/platform-project-sdk/client'
import type { CameraControlSystem, ImageRenderingSystem } from '@salsita-ai/platform-project-sdk/configurator'
import type { NavigationSystem, StoreSystem } from '@salsita-ai/platform-project-sdk/domain'
import { useLogger } from '@salsita-ai/platform-project-sdk/react'

import { synchronizeLiveCamera, synchronizeSceneForCapture, waitForRenderFrames } from '../../canvas/rendering-sync'
import { getDefaultCameraProps } from '../../entity-components/plate'
import { exportStepFile } from '../../export/oc'
import { configuratorInterface } from '../../systems/configurator-interface'
import { setQuoteId, useMetalworksApi } from '../../systems/metalworks-api'
import type { FinalizationFormData } from './shared/JobDetailsForm'

export enum FinalizationState {
  NotStarted = 'NotStarted',
  SavingConfiguration = 'SavingConfiguration',
  GeneratingImages = 'GeneratingImages',
  GeneratingDesign = 'GeneratingDesign',
  CreatingQuote = 'CreatingQuote',
  Done = 'Done',
}

export const createGenerateImages = (
  configurationId: string,
  storeSystem: StoreSystem<any, any, any, any, any, any>,
  imageRenderingSystem: ImageRenderingSystem<any, any, any>,
  cameraControlSystem: CameraControlSystem<any>,
  navigationSystem: NavigationSystem<any, any, any, any, any, any, any, any, any>,
  authorizedClient: AuthorizedPhoenixApiClient<any, any, any>
) => {
  return async (finalizationUuid: string, rollbackNavigationState: ApplicationTypes.NavigationState) => {
    const domainState = storeSystem.stateSlices.committed.value.state
    const plate = assertExistence(
      domainState.entities.find((entity: AnyDomainEntity) => entity.id === domainState.rootEntityId)
    ) as Plate

    const bend = domainState.entities.find((entity: AnyDomainEntity) => entity.type === CustomBend.type) as
      | CustomBend
      | undefined

    const frontCameraAzimuthAngle = plate.handing === Handing.standard ? 0 : Math.PI
    const frontCameraPolarAngle = Math.PI / 2
    const defaultCameraProps = getDefaultCameraProps(
      bend?.direction ?? null,
      bend?.orientation ?? null,
      plate.handing,
      0
    )

    const createGetCamera = (forceAzimuthAngle?: number, forcePolarAngle?: number) => async (scene: Scene) => {
      // TODO: Had to switch to PerspectiveCamera, because with OrthographicCamera
      // dimension indicators were not rendered correctly.
      const camera = new PerspectiveCamera(defaultCameraProps.fov, 1, defaultCameraProps.near, defaultCameraProps.far)
      const mesh = scene.getObjectByName(plate.id)
      if (mesh == null) {
        throw new Error('Root entity not found!')
      }

      const controls = new CameraControls(camera)
      await controls.fitToBox(mesh, false)
      await controls.rotateTo(
        forceAzimuthAngle ?? defaultCameraProps.azimuthAngle,
        forcePolarAngle ?? defaultCameraProps.polarAngle,
        false
      )

      controls.update(1000000) // To mock the "animations"
      await synchronizeLiveCamera(controls, cameraControlSystem.perspectiveCameraControls)
      await synchronizeSceneForCapture(scene)

      return camera
    }

    const captureImage = async (config: Parameters<typeof imageRenderingSystem.getGeneratedImages>[0][number]) => {
      const liveCameraState = cameraControlSystem.perspectiveCameraControls.toJSON()

      try {
        const [image] = await imageRenderingSystem.getGeneratedImages([config])
        return image!
      } finally {
        cameraControlSystem.perspectiveCameraControls.fromJSON(liveCameraState, false)
        cameraControlSystem.perspectiveCameraControls.update(1000000)
      }
    }

    const designImageConfig = {
      name: 'design',
      width: 1280,
      height: 1280,
      getCamera: bend
        ? createGetCamera(defaultCameraProps.azimuthAngle, defaultCameraProps.polarAngle)
        : createGetCamera(frontCameraAzimuthAngle, frontCameraPolarAngle),
    }

    const draftingImageConfig = {
      name: 'image',
      width: 720,
      height: 720,
      getCamera: createGetCamera(),
    }

    const designImage = await captureImage(designImageConfig)

    let unfoldedDesignImage: { name: string; blob: Blob } | undefined
    if (bend) {
      storeSystem.stageAndCommit([navigationSystem.getViewUpdate(ApplicationTypes.ViewState['2d'])])

      await waitForRenderFrames()

      const unfoldedDesignImageConfig = {
        name: CustomResource.Unfolded,
        width: 1280,
        height: 1280,
        getCamera: createGetCamera(frontCameraAzimuthAngle, frontCameraPolarAngle),
      }

      unfoldedDesignImage = await captureImage(unfoldedDesignImageConfig)
    }

    storeSystem.stageAndCommit([
      navigationSystem.getViewUpdate(ApplicationTypes.ViewState['3d']),
      navigationSystem.getNavigationUpdate(ApplicationTypes.NavigationState.FinalizationThumbnail),
    ])

    await waitForRenderFrames()

    const draftingImage = await captureImage(draftingImageConfig)

    storeSystem.stageAndCommit([navigationSystem.getNavigationUpdate(rollbackNavigationState)])

    const images = [designImage, unfoldedDesignImage, draftingImage].filter(isNonNullish)
    const links = await authorizedClient.generateUploadLinks(configurationId, finalizationUuid, [
      ...(unfoldedDesignImage?.name ? [unfoldedDesignImage.name] : []),
      designImageConfig.name,
      draftingImageConfig.name,
    ])

    const imageUploadPromise = Promise.all(
      links.map(async link => {
        const image = images.find(image => image.name === link.name)
        if (image == null) {
          throw new Error('Image not found.')
        }

        await fetch(link.url, {
          method: 'PUT',
          body: image.blob,
          headers: {
            'Content-Type': 'image/png',
            'Content-Length': image.blob.size.toString(),
          },
        })
      })
    )

    return {
      designImage: designImage!,
      draftingImage: draftingImage!,
      imageUploadPromise,
    }
  }
}

export const useFinalize = () => {
  const [returnUrl, setReturnUrl] = useState<string | null>(null)
  const [finalizationState, setFinalizationState] = useState<FinalizationState>(FinalizationState.NotStarted)

  const logger = useLogger()
  const { storeSystem, configurationId, navigationSystem, domain } = configuratorInterface.useDomain()
  const { cameraControlSystem, imageRenderingSystem } = configuratorInterface.useConfigurator()
  const { configurationPersistSystem } = configuratorInterface.useDomainApplication()
  const apiClient = configuratorInterface.useAuthorizedClient()

  const session = configuratorInterface.useSession()
  const sessionPayload = session.sessionPayload

  const generateImages = useMemo(
    () =>
      createGenerateImages(
        configurationId,
        storeSystem,
        imageRenderingSystem,
        cameraControlSystem,
        navigationSystem,
        apiClient
      ),
    [configurationId, storeSystem, imageRenderingSystem, cameraControlSystem, navigationSystem, apiClient]
  )

  const metalworksApi = useMetalworksApi()

  const finalize = useCallback(
    async (data: FinalizationFormData) => {
      setFinalizationState(FinalizationState.SavingConfiguration)

      const finalizationUuid = v4()

      const domainState = storeSystem.stateSlices.committed.value.state
      const $plate = domain.getRoot$Entity(domainState)

      await configurationPersistSystem.save()

      setFinalizationState(FinalizationState.GeneratingImages)

      const { imageUploadPromise } = await generateImages(
        finalizationUuid,
        ApplicationTypes.NavigationState.Finalization
      )

      // Step file generation blocks the main thread,
      // so we need to give the animation some time to finish.
      setFinalizationState(FinalizationState.GeneratingDesign)
      await new Promise(resolve => setTimeout(resolve, 100))

      let stepFileText = ''
      let volume = 0
      try {
        const generated = await exportStepFile(storeSystem.stateSlices.committed.value.state, domain)

        stepFileText = await generated.stepFile.text()
        volume = generated.volume
      } catch (error) {
        if (error instanceof Error) {
          logger.error('Failed to export STEP file.', {}, error)
        } else {
          logger.error('Failed to export STEP file.', { error })
        }
      }

      setFinalizationState(FinalizationState.CreatingQuote)

      let quoteId = data.quoteId
      const { finalizationType, quoteLineId } = data

      if (finalizationType === 'new') {
        const newQuoteData = await metalworksApi.createQuote({
          data: {
            theirReference: data.theirReference,
          },
        })

        quoteId = newQuoteData.id
        setReturnUrl(newQuoteData.link)
        setQuoteId(configurationId, quoteId)
      }

      if (quoteId === null) {
        throw new Error('Missing quote ID, should not happen.')
      }

      if (finalizationType === 'existingQuote' || finalizationType === 'existingQuoteLine') {
        const existingQuoteData = await metalworksApi.getQuote({ quoteId })

        setReturnUrl(existingQuoteData.link)
      }

      const selectedCustomMaterial = $plate.$selectedCustomMaterial
      if (!selectedCustomMaterial) {
        throw new Error('Custom material not found. This should happen only in dry-run mode.')
      }

      // Wait for images to be uploaded, we need them uploaded in the next step.
      await imageUploadPromise

      await apiClient.finalize(configurationId, finalizationUuid, {
        usage: data.usage,
        accessToken: sessionPayload?.data?.accessToken ?? '',
        stepFile: stepFileText,
        quoteId,
        quoteLineId,
        materialCode: $plate.$selectedCustomMaterial.code,
        rawMaterial: $plate.$selectedCustomMaterial.rawMaterial,
        usedCountersinks: $plate.$usedCountersinks,
        volume,
      })
      await apiClient.waitForFinalizationToComplete(configurationId, finalizationUuid)

      setFinalizationState(FinalizationState.Done)
    },
    [
      logger,
      configurationPersistSystem,
      configurationId,
      storeSystem.stateSlices.committed.value,
      apiClient,
      sessionPayload,
      generateImages,
      metalworksApi,
      domain,
    ]
  )

  const reset = useCallback(() => {
    setFinalizationState(FinalizationState.NotStarted)
    setReturnUrl(null)
  }, [])

  return { finalizationState, finalize, reset, returnUrl }
}
